import { CalendarToday, Person } from '@mui/icons-material';
import BurstModeOutlinedIcon from '@mui/icons-material/BurstModeOutlined';
import { Paper, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type FC, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { type CommentModel, CommentType, type IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { readableDateAndHour } from '../../utils';
import { LightboxGallery } from '../Dialogs';

const AvaniStyledPaperCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isPDF'
})<{ isPDF: boolean }>(({ theme, isPDF }) => {
  const currentTheme = isPDF ? AvaniLightTheme : theme;

  return `
      background-color: ${currentTheme.colors.avani.aGrey};
      border-radius: 16px;
      margin-top: 16px;
      padding: 16px;
    `;
});
interface AvaniPaperCardProps extends CommentModel, IIsPdf {
  outline?: boolean;
}

export const AvaniPaperCard: FC<AvaniPaperCardProps> = ({
  content,
  authorName,
  interviewedName,
  outline,
  createdAt,
  type,
  medias,
  isPDF = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const [toggle, setToggle] = useState(false);
  const handleClose = () => {
    setToggle(false);
  };

  return (
    <AvaniStyledPaperCard
      isPDF={isPDF}
      elevation={0}
      sx={{ border: outline ? `1px solid ${theme.colors.avani.darkTitleText}` : 'none', mb: outline ? 1 : 0 }}
    >
      <div style={{ float: 'left', marginRight: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <svg width="60" height="49" viewBox="0 0 60 49" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M24 33.5015V36.5015C24 39.8108 26.6906 42.5015 30 42.5015H40.9969L48.6 48.2015C49.05 48.539 49.6594 48.5952 50.175 48.3421C50.6906 48.089 51 47.5733 51 47.0015V42.5015H54C57.3094 42.5015 60 39.8108 60 36.5015V18.5015C60 15.1921 57.3094 12.5015 54 12.5015H42V24.5015C42 29.4702 37.9688 33.5015 33 33.5015H24Z"
              fill="#00A7A8"
            />
            <path
              d="M0 6.50049C0 3.19111 2.69063 0.500488 6 0.500488H33C36.3094 0.500488 39 3.19111 39 6.50049V24.5005C39 27.8099 36.3094 30.5005 33 30.5005H19.0031L11.4 36.2005C10.95 36.538 10.3406 36.5942 9.825 36.3411C9.30938 36.088 9 35.5724 9 35.0005V30.5005H6C2.69063 30.5005 0 27.8099 0 24.5005V6.50049Z"
              fill="#005665"
            />
          </svg>
          {!!medias?.length && (
            <BurstModeOutlinedIcon
              fontSize={'large'}
              sx={{
                marginRight: '0.5rem',
                marginTop: '-0.5rem',
                color: theme.colors.avani.teal,
                cursor: 'pointer',
                '&:hover': { color: theme.colors.avani.magenta }
              }}
              onClick={() => setToggle(true)}
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', width: 'auto', alignItems: 'center', justifyContent: 'space-between' }}>
        {!!authorName && (
          <Typography
            variant="body2"
            lineHeight={1}
            fontWeight={'bold'}
            sx={{ display: 'inline-block', marginRight: 1, marginBottom: 1 }}
          >
            <Person color={'aTurc'} style={{ float: 'left', marginTop: -5 }} fontSize={'small'} />{' '}
            {type === CommentType.PreCleanClient ? interviewedName : authorName}
          </Typography>
        )}
        {outline && createdAt && (
          <Typography variant="body2" fontWeight={'bold'} sx={{ display: 'inline-block', float: 'center' }}>
            <CalendarToday
              color={'aTurc'}
              fontSize={'small'}
              style={{ float: 'left', marginTop: -3, marginRight: 3 }}
            />
            {readableDateAndHour(createdAt)}
          </Typography>
        )}

        {!!outline && (
          <Typography component={'span'} variant={'body1'} sx={{ float: 'right', color: theme.colors.avani.teal }}>
            {type}
          </Typography>
        )}
      </div>
      {!!medias?.length && (
        <LightboxGallery open={toggle} index={0} handleClose={handleClose} imageSrc={medias} theme={theme} />
      )}

      {!!content && (
        <Typography variant="body2">
          <ReactMarkdown>{content}</ReactMarkdown>
        </Typography>
      )}
    </AvaniStyledPaperCard>
  );
};
